import { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getSimilarProfiles, SortOption } from '../../api/profile';
import { getAPIConfig } from '../../api/config';
import { ProfileResponse } from '../../utils/types';
import DiscoverMainSection from '../../components/Discover/DiscoverMainSection/index';
import ErrorBoundary from '../../components/ErrorBoundary';
import { useTicker } from '../../context/TickerContext';
import { TICKER_MESSAGES } from '../../components/Discover/Ticker';
import './styles/mobile.css';
import { resetScrollPositions } from '../../utils/scrollUtils';
import { PROMO_CONFIG } from '../../config/promotions';

// Extend ProfileResponse to include relevanceScore
interface EnhancedProfileResponse extends ProfileResponse {
    relevanceScore?: number;
    _originalScore?: number;
    backendRandomized?: boolean; // Add flag to check if backend has already applied randomization
    user?: {
        username?: string;
    };
}

export default function Discover() {
    const { setText } = useTicker();
    const user = useSelector((state: any) => state.auth.user);
    const profile = useSelector((state: any) => state.auth.profile);
    const [sortBy, setSortBy] = useState<SortOption>('relevance');
    const [viewMode, setViewMode] = useState<'list' | 'grid'>(() => {
        // Try to get saved preference from localStorage, default to 'list'
        const savedViewMode = localStorage.getItem(PROMO_CONFIG.SESSION_KEYS.DISCOVER_VIEW_MODE);
        return (savedViewMode === 'list' || savedViewMode === 'grid') ? savedViewMode : 'list';
    });
    const [isMobile, setIsMobile] = useState(false);
    const [randomizeProfiles, setRandomizeProfiles] = useState(false);
    const [randomFactorMin, setRandomFactorMin] = useState(0);
    const [randomFactorMax, setRandomFactorMax] = useState(1.5); // Updated to 1.5 to match other components
    const [configLoaded, setConfigLoaded] = useState(false);
    const [configError, setConfigError] = useState<Error | null>(null);
    // Add a ref to track loading state
    const isLoadingMoreRef = useRef(false);
    // Add a ref to track the number of pages loaded
    const loadedPagesRef = useRef(0);
    const maxPagesPerBatchRef = useRef(1); // Only load 1 page at a time
    // Add a ref to track the previous sort value for comparison
    const prevSortRef = useRef<SortOption | null>(null);

    // Fetch configuration settings when component mounts
    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const config = await getAPIConfig();

                // Explicitly set values from config
                const shouldRandomize = config.RANDOMIZE_DISCOVER_PAGE === true;
                const minFactor = typeof config.RANDOMNESS_FACTOR_MIN === 'number'
                    ? config.RANDOMNESS_FACTOR_MIN
                    : 0.0;
                const maxFactor = typeof config.RANDOMNESS_FACTOR_MAX === 'number'
                    ? config.RANDOMNESS_FACTOR_MAX
                    : 1.5;

                setRandomizeProfiles(shouldRandomize);
                setRandomFactorMin(minFactor);
                setRandomFactorMax(maxFactor);

                setConfigLoaded(true);
                setConfigError(null);
            } catch (error) {
                console.error('Error fetching configuration:', error);
                setConfigError(error as Error);
                // Keep default values if error
                setConfigLoaded(true); // Still mark as loaded to continue
            }
        };
        fetchConfig();
    }, []);

    // Calculate effective view mode (always list on mobile)
    const effectiveViewMode = isMobile ? 'list' : viewMode;

    // Determine limit based on view mode
    const getLimit = useCallback(() => {
        if (isMobile) return 25; // Mobile is always list view
        return viewMode === 'grid' ? 32 : 25; // 32 for grid view, 25 for list view
    }, [viewMode, isMobile]);

    // Check if we're on a mobile device
    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobile(window.innerWidth < PROMO_CONFIG.MOBILE_BREAKPOINT);
        };

        // Initial check
        checkIfMobile();

        window.addEventListener('resize', checkIfMobile);
        return () => window.removeEventListener('resize', checkIfMobile);
    }, []);

    const fetchProfiles = useCallback(async ({ pageParam = 0 }) => {
        try {
            const limit = getLimit();
            const response = await getSimilarProfiles(user._id, pageParam, sortBy, limit);
            // Pass along the recentSortLimitReached flag to the infinite query
            return response;
        } catch (error) {
            console.error(error);
            throw error;
        }
    }, [user?._id, sortBy, getLimit]);

    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, status, refetch } =
        useInfiniteQuery({
            queryKey: ['similarProfiles', sortBy, profile?._id, JSON.stringify({
                skills: profile?.skills || [],
                location: profile?.location || null,
                headline: profile?.headline || null,
                favArtists: profile?.favArtists || [],
                favGenres: profile?.favGenres || []
            })],
            queryFn: fetchProfiles,
            getNextPageParam: (lastPage) => {
                // If we've hit the recent sort limit, don't allow more pages
                if (lastPage.recentSortLimitReached) {
                    return null; // This will set hasNextPage to false
                }
                return lastPage.nextPage;
            },
            refetchOnMount: true,
            refetchOnWindowFocus: false,
            enabled: !!user?._id,
        });

    // Deduplicate profiles by _id
    const allProfiles = data ?
        data.pages.flatMap((page) => page.profiles)
            .filter((profile, index, self) =>
                index === self.findIndex((p) => p._id === profile._id)
            ) as EnhancedProfileResponse[]
        : [] as EnhancedProfileResponse[];

    // Keep track of the current profile order with a ref to maintain consistent ordering across renders
    const profileOrderRef = useRef<string[]>([]);

    // Apply randomization to profiles if enabled - but only once per profile
    const randomizedProfiles = useCallback((profiles: EnhancedProfileResponse[]) => {
        if (!profiles || profiles.length === 0) return profiles;

        // Check if backend has already applied randomization
        const backendRandomized = profiles.length > 0 && profiles[0].backendRandomized;

        // Create a copy to avoid mutating the original data
        const profilesCopy = [...profiles];

        // If the backend randomized profiles, just use those profiles as-is
        // This will ensure we get freshly randomized results on each refresh/load
        if (backendRandomized) {
            // For refresh/new loads, reset our tracked order to match the backend-randomized order
            if (profileOrderRef.current.length === 0) {
                profileOrderRef.current = profilesCopy.map(p => p._id);
                return profilesCopy;
            }
        } else if (!randomizeProfiles) {
            // If randomization is disabled, just return profiles as-is
            if (profileOrderRef.current.length === 0) {
                profileOrderRef.current = profilesCopy.map(p => p._id);
            }
            return profilesCopy;
        } else {
            // If backend did not randomize, but we need to randomize on the frontend

            // If this is a new load or refresh, we randomize everything
            if (profileOrderRef.current.length === 0) {
                profilesCopy.forEach(profile => {
                    // Generate a random factor between min and max from config
                    const randomFactor = randomFactorMin + Math.random() * (randomFactorMax - randomFactorMin);

                    // Store original score for debugging
                    profile._originalScore = profile.relevanceScore;

                    // Add the random factor to the relevance score
                    if (typeof profile.relevanceScore === 'number') {
                        profile.relevanceScore += randomFactor;
                    }
                });

                // Sort everything by the new randomized score
                profilesCopy.sort((a, b) => (b.relevanceScore || 0) - (a.relevanceScore || 0));

                // Track the new order
                profileOrderRef.current = profilesCopy.map(p => p._id);

                return profilesCopy;
            }
        }

        // For pagination (new profiles added to existing set), we need to handle both backend and frontend randomization
        // Identify new profiles (profiles not in our tracked order)
        const existingProfileIds = profileOrderRef.current;
        const newProfiles = profilesCopy.filter(p => !existingProfileIds.includes(p._id));
        const existingProfiles = profilesCopy.filter(p => existingProfileIds.includes(p._id));

        if (newProfiles.length > 0) {
            // For backend randomized profiles, we should respect the order they come in
            if (!backendRandomized && randomizeProfiles) {
                // Only apply frontend randomization if backend hasn't done it
                newProfiles.forEach(profile => {
                    // Generate a random factor between min and max from config
                    const randomFactor = randomFactorMin + Math.random() * (randomFactorMax - randomFactorMin);

                    // Store original score for debugging
                    profile._originalScore = profile.relevanceScore;

                    // Add the random factor to the relevance score
                    if (typeof profile.relevanceScore === 'number') {
                        profile.relevanceScore += randomFactor;
                    }
                });

                // Sort new profiles by randomized score
                newProfiles.sort((a, b) => (b.relevanceScore || 0) - (a.relevanceScore || 0));
            }

            // Add new profile IDs to our tracking
            profileOrderRef.current = [...existingProfileIds, ...newProfiles.map(p => p._id)];
        }

        // Sort the entire collection according to our tracked order to maintain consistency
        profilesCopy.sort((a, b) => {
            const indexA = profileOrderRef.current.indexOf(a._id);
            const indexB = profileOrderRef.current.indexOf(b._id);

            // If both exist in the order, respect that order
            if (indexA >= 0 && indexB >= 0) {
                return indexA - indexB;
            }

            // If only one exists in the order, prioritize it
            if (indexA >= 0) return -1;
            if (indexB >= 0) return 1;

            // If neither exists in the order, sort by score
            return (b.relevanceScore || 0) - (a.relevanceScore || 0);
        });

        return profilesCopy;
    }, [randomizeProfiles, randomFactorMin, randomFactorMax]);

    // Get the final profiles to display - only apply randomization if config is loaded
    const displayProfiles = configLoaded && randomizeProfiles
        ? randomizedProfiles(allProfiles)
        : allProfiles;

    const handleSortChange = useCallback((newSortBy: SortOption) => {
        // Check if we're actually changing the sort
        if (newSortBy !== sortBy) {
            // Update state
            setSortBy(newSortBy);

            // Reset ref for profile ordering when sort changes
            profileOrderRef.current = [];

            // Reset page tracking
            loadedPagesRef.current = 0;

            // Use the utility function to reset all scroll positions
            resetScrollPositions();

            // Reset the query cache and refetch with a small delay
            setTimeout(() => {
                refetch();
            }, 10);
        }
    }, [sortBy, refetch]);

    const handleViewModeChange = useCallback((newViewMode: 'list' | 'grid') => {
        setViewMode(newViewMode);
        // Save preference to localStorage
        localStorage.setItem(PROMO_CONFIG.SESSION_KEYS.DISCOVER_VIEW_MODE, newViewMode);
        // Don't refetch data when changing view modes
        // Let the infinite scroll handle loading more data when needed
    }, []);

    const loadMore = useCallback(() => {
        // Only proceed if we have more pages and aren't already fetching
        if (hasNextPage && !isFetchingNextPage) {
            // Fetch the next page of data
            fetchNextPage()
                .then(() => {
                    // Increment the counter of loaded pages
                    loadedPagesRef.current += 1;
                })
                .catch((error) => {
                    console.error('Discover component: Error fetching more profiles:', error);
                });
        }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    useEffect(() => {
        setText(TICKER_MESSAGES.DISCOVER.messages[0]);
    }, [setText]);

    // Automatically refetch when sort changes
    useEffect(() => {
        // Only refetch if we already had a previous sort and it's different
        if (prevSortRef.current !== null && prevSortRef.current !== sortBy) {
            // Wait for React to complete updates before refetching
            setTimeout(() => {
                refetch();
            }, 0);
        }

        // Update the previous sort ref
        prevSortRef.current = sortBy;
    }, [sortBy, refetch]);

    // Create a container key that changes when sort changes to force remount
    const containerKey = `profiles-container-${sortBy}`;

    return (
        <ErrorBoundary>
            <DiscoverMainSection
                key={containerKey}
                profiles={displayProfiles}
                onSortChange={handleSortChange}
                isLoading={status === 'loading'}
                loadMore={loadMore}
                hasMore={hasNextPage || false}
                isFetchingMore={isFetchingNextPage}
                currentViewMode={viewMode}
                onViewModeChange={handleViewModeChange}
                isMobile={isMobile}
                currentSortBy={sortBy}
            />
        </ErrorBoundary>
    );
}
